/**
 * Input Normalization & Gesture Parser
 * Unified Pointer API handling mouse/touch gestures with 5-frame velocity sampling.
 */

import { EventBus, EventType } from '../core/EventBus';

export interface PointerState {
  x: number;          // Normalized screen space [-1, 1]
  y: number;          // Normalized screen space [-1, 1]
  clientX: number;    // Raw pixel coordinate
  clientY: number;
  isDown: boolean;
  velocityX: number;  // 5-frame moving average
  velocityY: number;
  speed: number;
}

export class InputSystem {
  private eventBus: EventBus;
  private pointer: PointerState;
  
  // 5-frame velocity window history
  private historyX: number[];
  private historyY: number[];
  private historyTime: number[];
  private historyIndex: number;

  private ariaLiveRegion: HTMLElement | null = null;

  constructor() {
    this.eventBus = EventBus.getInstance();
    this.pointer = {
      x: 0,
      y: 0,
      clientX: 0,
      clientY: 0,
      isDown: false,
      velocityX: 0,
      velocityY: 0,
      speed: 0
    };

    this.historyX = new Array(5).fill(0);
    this.historyY = new Array(5).fill(0);
    this.historyTime = new Array(5).fill(0);
    this.historyIndex = 0;
  }

  public init(canvas: HTMLCanvasElement): void {
    this.setupAriaLiveRegion();

    const onPointerDown = (e: PointerEvent) => {
      this.pointer.isDown = true;
      this.updateCoordinates(e, canvas);
      this.resetHistory(e.clientX, e.clientY);

      this.eventBus.emit(
        EventType.INPUT_POINTER_DOWN,
        this.pointer.x,
        this.pointer.y
      );
    };

    const onPointerMove = (e: PointerEvent) => {
      this.updateCoordinates(e, canvas);
      this.recordSample(e.clientX, e.clientY, performance.now());
      this.computeVelocity();

      if (this.pointer.isDown) {
        const dx = this.pointer.velocityX;
        const dy = this.pointer.velocityY;
        const vel = this.pointer.speed;

        this.eventBus.emit(
          EventType.INPUT_POINTER_MOVE,
          this.pointer.x,
          this.pointer.y,
          dx,
          dy,
          vel
        );

        if (vel > 120) {
          this.eventBus.emit(
            EventType.INPUT_GESTURE_STRIKE,
            this.pointer.x,
            this.pointer.y,
            dx,
            dy,
            vel
          );
        }
      }
    };

    const onPointerUp = (e: PointerEvent) => {
      this.pointer.isDown = false;
      this.updateCoordinates(e, canvas);

      this.eventBus.emit(
        EventType.INPUT_POINTER_UP,
        this.pointer.x,
        this.pointer.y
      );
    };

    canvas.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);

    // Keyboard Accessibility Mapping
    window.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.code === 'Space' || e.code === 'Enter') {
        this.eventBus.emit(
          EventType.INPUT_GESTURE_STRIKE,
          0, 0, 0, 100, 300
        );
      } else if (e.code === 'ArrowRight' || e.code === 'KeyD') {
        this.eventBus.emit(
          EventType.INPUT_POINTER_MOVE,
          0.5, 0, 100, 0, 100
        );
      } else if (e.code === 'ArrowLeft' || e.code === 'KeyA') {
        this.eventBus.emit(
          EventType.INPUT_POINTER_MOVE,
          -0.5, 0, -100, 0, 100
        );
      }
    });

    // Subscribe to ARIA Announcements
    this.eventBus.subscribe(EventType.ACCESSIBILITY_ANNOUNCE, (payload) => {
      if (payload.text && this.ariaLiveRegion) {
        this.ariaLiveRegion.textContent = payload.text;
      }
    });
  }

  public getPointer(): PointerState {
    return this.pointer;
  }

  private updateCoordinates(e: PointerEvent, canvas: HTMLCanvasElement): void {
    const rect = canvas.getBoundingClientRect();
    this.pointer.clientX = e.clientX;
    this.pointer.clientY = e.clientY;

    // Normalize to [-1.0, 1.0]
    this.pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    this.pointer.y = -(((e.clientY - rect.top) / rect.height) * 2 - 1);
  }

  private resetHistory(x: number, y: number): void {
    const now = performance.now();
    for (let i = 0; i < 5; i++) {
      this.historyX[i] = x;
      this.historyY[i] = y;
      this.historyTime[i] = now;
    }
    this.historyIndex = 0;
    this.pointer.velocityX = 0;
    this.pointer.velocityY = 0;
    this.pointer.speed = 0;
  }

  private recordSample(x: number, y: number, time: number): void {
    this.historyIndex = (this.historyIndex + 1) % 5;
    this.historyX[this.historyIndex] = x;
    this.historyY[this.historyIndex] = y;
    this.historyTime[this.historyIndex] = time;
  }

  private computeVelocity(): void {
    const oldestIdx = (this.historyIndex + 1) % 5;
    const dt = (this.historyTime[this.historyIndex] - this.historyTime[oldestIdx]) / 1000;

    if (dt > 0.001) {
      const dx = this.historyX[this.historyIndex] - this.historyX[oldestIdx];
      const dy = this.historyY[this.historyIndex] - this.historyY[oldestIdx];
      this.pointer.velocityX = dx / dt;
      this.pointer.velocityY = dy / dt;
      this.pointer.speed = Math.sqrt(this.pointer.velocityX ** 2 + this.pointer.velocityY ** 2);
    }
  }

  private setupAriaLiveRegion(): void {
    let elem = document.getElementById('aria-live-announcer');
    if (!elem) {
      elem = document.createElement('div');
      elem.id = 'aria-live-announcer';
      elem.setAttribute('aria-live', 'polite');
      elem.setAttribute('aria-atomic', 'true');
      elem.className = 'sr-only';
      document.body.appendChild(elem);
    }
    this.ariaLiveRegion = elem;
  }
}
